import React, { useRef, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'components/Select';
import { Text, Flex, Hide } from '@qga/roo-ui/lib/components';
import { themeGet } from 'styled-system';
import { SORT_SELECT_OPTIONS } from 'lib/enums/search';
import { getQuerySortBy } from 'store/router/routerSelectors';
import { getResultCount } from 'store/search/searchSelectors';
import { updateQuery } from 'store/search/searchActions';

const StyledFlex = styled(Hide)`
  display: flex;
  flex-direction: column;
  color: ${themeGet('colors.brand.primary')};
  margin-bottom: 2rem;
  padding: 0 1rem;
  border-bottom: ${themeGet('borders.1')} ${themeGet('colors.greys.alto')};

  ${themeGet('mediaQueries.0')} {
    flex-direction: row;
  }

  & select + div {
    display: flex;
  }
`;

const StyledSelect = styled(Select)`
  border-radius: ${themeGet('radii.default')};
  border: none;
  background-color: transparent;
  cursor: pointer;
  ::-ms-expand {
    display: none;
  }

  &:focus {
    box-shadow: 0 0 0 1pt ${themeGet('colors.ui.linkFocus')};
  }

  padding-left: 0.25rem;
  width: var(--select-width, 100%);
`;

const MobileSortSelect = React.memo(() => {
  const sortBy = useSelector(getQuerySortBy);
  const resultsCount = useSelector(getResultCount) ?? 0;
  const dispatch = useDispatch();

  const onChange = ({ target: { value } }) => {
    dispatch(updateQuery({ sortBy: value }));
  };

  const container = useRef();

  const updateSelectWidth = (label) => {
    const select = container?.current?.querySelector('select');
    if (!select) return;

    const nselect = document.createElement('span');
    if (select.computedStyleMap) {
      nselect.style.setProperty('fontSize', select.computedStyleMap().get('font-size').toString());
    } else {
      // Firefox Fallback
      const fontSize = window.getComputedStyle(select).fontSize;
      nselect.style.fontSize = fontSize;
    }
    nselect.innerText = label;

    document.body.appendChild(nselect);
    container?.current?.style.setProperty('--select-width', `${nselect.offsetWidth}px`);
    document.body.removeChild(nselect);
  };

  useLayoutEffect(() => {
    const label = SORT_SELECT_OPTIONS?.find((x) => x.value === sortBy)?.label;
    if (!label) return;

    updateSelectWidth(label);

    if (!document.fonts.check('12px Ciutadella')) {
      const handleFontLoadingDone = () => {
        updateSelectWidth(label);
        document.fonts.removeEventListener('loadingdone', handleFontLoadingDone);
      };

      document.fonts.addEventListener('loadingdone', handleFontLoadingDone);
    }
  }, [sortBy]);

  return (
    <StyledFlex sm md lg>
      <Flex width={['100%', '70%']} justifyContent="flex-start" ref={container}>
        <Text lineHeight={2.7} color="greys.charcoal" fontSize="18px">
          Sort by:
        </Text>
        <StyledSelect
          value={sortBy}
          options={SORT_SELECT_OPTIONS}
          onChange={onChange}
          aria-label="Sort search results"
          data-testid="sort-select"
          style={{ boxSizing: 'content-box' }}
        />
      </Flex>
      <Flex width={['100%', '30%']} justifyContent={['lex-end', 'flex-end']}>
        <Text textAlign="right" lineHeight={2.7} color="greys.charcoal" fontSize="18px" data-testid="mobile-filters-description">
          {resultsCount} properties
        </Text>
      </Flex>
    </StyledFlex>
  );
});

MobileSortSelect.displayName = 'MobileSortSelect';

export default MobileSortSelect;
